/**
 * palais-header.js
 *
 * Called by: sections/header.liquid
 *
 * Handles four responsibilities for the site header:
 *   1. Nav drawer: open/close, overlay click, ESC, focus trap, focus restore.
 *   2. Inline search bar: the search icon toggles a drop-down input in place
 *      instead of navigating to the search page (ESC / outside-click close it).
 *   3. Sticky behavior: adds data-condensed="true" when scrolled past threshold.
 *   4. Cart count badge: exposes window.palaisUpdateCartCount(n) for other
 *      scripts (Add-to-Cart, cart drawer) to call after Cart API mutations.
 */

(function () {
  'use strict';

  // ---------- Drawer ----------
  var header = document.querySelector('[data-palais-header]');
  var drawer = document.querySelector('[data-palais-drawer]');
  var overlay = document.querySelector('[data-palais-drawer-overlay]');
  var openBtn = document.querySelector('[data-palais-drawer-open]');
  var closeBtns = document.querySelectorAll('[data-palais-drawer-close]');

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var lastFocus = null;

  function openDrawer() {
    if (!drawer || !overlay) return;
    lastFocus = document.activeElement;
    overlay.hidden = false;
    // Force a reflow so the opacity transition runs.
    void overlay.offsetWidth;
    overlay.setAttribute('data-open', 'true');
    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('palais--lock');

    var firstFocusable = drawer.querySelector(FOCUSABLE);
    (firstFocusable || drawer).focus();

    document.addEventListener('keydown', onKeydown);
  }

  function closeDrawer() {
    if (!drawer || !overlay) return;
    overlay.removeAttribute('data-open');
    drawer.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('palais--lock');
    document.removeEventListener('keydown', onKeydown);

    // Hide overlay after transition so it stops intercepting clicks.
    setTimeout(function () { overlay.hidden = true; }, 400);

    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (e.key !== 'Tab' || !drawer) return;

    var focusables = drawer.querySelectorAll(FOCUSABLE);
    if (focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (openBtn) openBtn.addEventListener('click', openDrawer);
  closeBtns.forEach(function (btn) { btn.addEventListener('click', closeDrawer); });
  if (overlay) overlay.addEventListener('click', closeDrawer);

  // ---------- Inline search bar ----------
  // The header search icon toggles this in-place input instead of navigating
  // to the search results page; the form still submits to routes.search_url on
  // Enter. Same reflow trick as the drawer: drop the [hidden] attribute, force
  // a reflow, then set data-open so the max-height/opacity transition runs.
  var searchBar = document.querySelector('[data-palais-search]');
  var searchToggles = document.querySelectorAll('[data-palais-search-toggle]');
  var searchInput = document.querySelector('[data-palais-search-input]');

  function isSearchOpen() {
    return !!searchBar && searchBar.getAttribute('data-open') === 'true';
  }

  function openSearch() {
    if (!searchBar) return;
    searchBar.hidden = false;
    void searchBar.offsetWidth; // reflow so the transition runs from collapsed
    searchBar.setAttribute('data-open', 'true');
    searchToggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
    if (searchInput) searchInput.focus();
    document.addEventListener('keydown', onSearchKeydown);
    document.addEventListener('click', onSearchDocClick, true);
  }

  function closeSearch() {
    if (!searchBar) return;
    searchBar.removeAttribute('data-open');
    searchToggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    document.removeEventListener('keydown', onSearchKeydown);
    document.removeEventListener('click', onSearchDocClick, true);
    // Re-hide after the collapse transition so the input leaves the tab order.
    setTimeout(function () { if (!isSearchOpen()) searchBar.hidden = true; }, 300);
  }

  function onSearchKeydown(e) {
    if (e.key === 'Escape') {
      closeSearch();
      var firstToggle = searchToggles[0];
      if (firstToggle && typeof firstToggle.focus === 'function') firstToggle.focus();
    }
  }

  function onSearchDocClick(e) {
    // Close on a click outside the bar and outside any toggle button.
    if (!isSearchOpen()) return;
    if (searchBar.contains(e.target)) return;
    var onToggle = false;
    searchToggles.forEach(function (t) { if (t.contains(e.target)) onToggle = true; });
    if (!onToggle) closeSearch();
  }

  searchToggles.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (isSearchOpen()) { closeSearch(); } else { openSearch(); }
    });
  });

  // ---------- Sticky condense ----------
  if (header && header.getAttribute('data-sticky') !== 'false') {
    var ticking = false;
    var threshold = 80;

    function updateCondensed() {
      var condensed = window.scrollY > threshold;
      if (condensed) {
        header.setAttribute('data-condensed', 'true');
      } else {
        header.removeAttribute('data-condensed');
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(updateCondensed);
        ticking = true;
      }
    }, { passive: true });

    // Set initial state — e.g. after a navigation that lands mid-page.
    updateCondensed();
  }

  // ---------- Cart count ----------
  window.palaisUpdateCartCount = function (count) {
    var badge = document.querySelector('[data-palais-cart-count]');
    if (!badge) return;
    var n = parseInt(count, 10) || 0;
    badge.textContent = String(n);
    if (n === 0) {
      badge.setAttribute('hidden', '');
    } else {
      badge.removeAttribute('hidden');
    }
    // Also update the cart link aria-label so screen readers reflect changes.
    var link = badge.closest('[aria-label]');
    if (link) {
      link.setAttribute('aria-label', 'Cart, ' + n + (n === 1 ? ' item' : ' items'));
    }
  };
})();
